export class Crypto {
  private static readonly DEFAULT_PREFIX = 'EncryptedValue';
  readonly encryptionInstance: PIIEncryption;

  private constructor(prefix: string, aesKey: string) {
    const finalPrefix: string = SDKHelper.isBlank(prefix) ? Crypto.DEFAULT_PREFIX : prefix;

    if (aesKey == null) {
      throw new SmsRuntimeException('AES key can not be null.');
    }

    this.encryptionInstance = PIIEncryption.initializeSdk(finalPrefix, aesKey);
  }

  public static initialize(prefix: string, aesKey: string): Crypto {
    return new Crypto(prefix, aesKey);
  }

  public async encrypt(plainText: string): Promise<string> {
    return await this.encryptionInstance.encrypt(plainText);
  }
}

class SDKHelper {
  static isBlank(value: string): boolean {
    if (value == null || value.trim() === '') {
      return true;
    }
    return false;
  }

  static generateIv(): Uint8Array {
    const randomBytes = crypto.getRandomValues(new Uint8Array(16));
    randomBytes[6] &= 0x0f;
    randomBytes[6] |= 0x40;
    randomBytes[8] &= 0x3f;
    randomBytes[8] |= 0x80;

    var significantBitsA: number = 0;
    var significantBitsB: number = 0;

    for (let i = 0; i < 4; i++) {
      significantBitsA = (significantBitsA << 8) | (randomBytes[i] & 0xff);
    }

    for (let i = 4; i < 8; i++) {
      significantBitsB = (significantBitsB << 8) | (randomBytes[i] & 0xff);
    }

    const partA = SDKHelper.numberToBytes(significantBitsA);
    const partB = SDKHelper.numberToBytes(significantBitsB);
    const iv = crypto.getRandomValues(new Uint8Array(4));

    return new Uint8Array([...partA, ...partB, ...iv]);
  }

  static numberToBytes(num: number): Uint8Array {
    const bytes = new Uint8Array(4);
    for (let i = 0; i < 4; i++) {
      bytes[i] = (num >> (i * 8)) & 0xff;
    }
    return bytes;
  }

  static getRawAesKey(plainAesKey: string): Uint8Array {
    try {
      let rawKey = atob(plainAesKey);
      let rawKeyBytes = new Uint8Array(rawKey.length);
      for (let i = 0; i < rawKey.length; i++) {
        rawKeyBytes[i] = rawKey.charCodeAt(i);
      }
      return rawKeyBytes;
    } catch (exception) {
      if (exception instanceof Error) {
        throw new SmsRuntimeException(exception.message);
      } else {
        throw exception;
      }
    }
  }
}

class PIIEncryption {
  readonly prefix: string;
  readonly aesKey: Uint8Array;
  private static readonly ENC_ALG: string = 'AES-GCM';

  private constructor(prefix: string, aesKey: string) {
    this.prefix = prefix;
    this.aesKey = SDKHelper.getRawAesKey(aesKey);
  }

  static initializeSdk(prefix: string, aesKey: string): PIIEncryption {
    return new PIIEncryption(prefix, aesKey);
  }

  async encrypt(plainText: string): Promise<string> {
    try {
      if (plainText == null) {
        return plainText;
      }

      const encodedData = new TextEncoder().encode(plainText);
      const iv = SDKHelper.generateIv();

      const cryptKey = await crypto.subtle.importKey('raw', this.aesKey, { name: PIIEncryption.ENC_ALG }, false, [
        'encrypt',
        'decrypt',
      ]);
      const cipherText = await crypto.subtle.encrypt({ name: PIIEncryption.ENC_ALG, iv }, cryptKey, encodedData);

      return this.finalString(iv, new Uint8Array(cipherText));
    } catch (exception) {
      if (exception instanceof Error) {
        throw new SmsRuntimeException(exception.message);
      } else {
        throw exception;
      }
    }
  }

  private finalString(iv: Uint8Array, cipherText: Uint8Array): string {
    return this.prefix + '.' + btoa(String.fromCharCode.apply(null, Array.from(new Uint8Array([...iv, ...cipherText]))));
  }
}

export class SmsRuntimeException extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'SmsRuntimeException';
  }
}
